import { makeAutoObservable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import type { RootStore } from "./root.store";
import type {
  ICreateIdentityProvider,
  IWorkspaceDomain,
  IWorkspaceIdentityProvider,
} from "@/types/workspace";

export class WorkspaceSsoStore {
  domains: IWorkspaceDomain[] = [];
  providers: IWorkspaceIdentityProvider[] = [];
  isLoadingDomains = false;
  isLoadingProviders = false;
  isSavingDomain = false;
  activeDomainId: string | null = null;
  activeProviderId: string | null = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get samlProvider(): IWorkspaceIdentityProvider | undefined {
    return this.providers.find((provider) => provider.provider === "saml");
  }

  get oidcProvider(): IWorkspaceIdentityProvider | undefined {
    return this.providers.find((provider) => provider.provider === "oidc");
  }

  async fetchDomains(slug: string): Promise<IWorkspaceDomain[]> {
    this.isLoadingDomains = true;
    try {
      const domains = await this.rootStore.workspaceSsoService.getDomains(slug);
      runInAction(() => {
        this.domains = domains;
      });
      return domains;
    } finally {
      runInAction(() => {
        this.isLoadingDomains = false;
      });
    }
  }

  async createDomain(
    slug: string,
    domain: string,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceDomain> {
    this.isSavingDomain = true;
    try {
      const created = await this.rootStore.workspaceSsoService.createDomain(slug, { domain });
      runInAction(() => {
        this.domains = [...this.domains, created];
      });
      track("sso_domain_created", { domain_id: created.id, source });
      return created;
    } finally {
      runInAction(() => {
        this.isSavingDomain = false;
      });
    }
  }

  async updateDomain(
    slug: string,
    domainId: string,
    data: { is_sso_enabled: boolean },
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceDomain> {
    this.activeDomainId = domainId;
    try {
      const updated = await this.rootStore.workspaceSsoService.updateDomain(slug, domainId, data);
      runInAction(() => {
        this.domains = this.domains.map((domain) => (domain.id === updated.id ? updated : domain));
      });
      track(data.is_sso_enabled ? "sso_domain_enabled" : "sso_domain_disabled", {
        domain_id: domainId,
        source,
      });
      return updated;
    } finally {
      runInAction(() => {
        this.activeDomainId = null;
      });
    }
  }

  async deleteDomain(
    slug: string,
    domainId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeDomainId = domainId;
    try {
      await this.rootStore.workspaceSsoService.deleteDomain(slug, domainId);
      runInAction(() => {
        this.domains = this.domains.filter((domain) => domain.id !== domainId);
      });
      track("sso_domain_deleted", { domain_id: domainId, source });
    } finally {
      runInAction(() => {
        this.activeDomainId = null;
      });
    }
  }

  async fetchProviders(slug: string): Promise<IWorkspaceIdentityProvider[]> {
    this.isLoadingProviders = true;
    try {
      const providers = await this.rootStore.workspaceSsoService.getProviders(slug);
      runInAction(() => {
        this.providers = providers;
      });
      return providers;
    } finally {
      runInAction(() => {
        this.isLoadingProviders = false;
      });
    }
  }

  async createProvider(
    slug: string,
    data: ICreateIdentityProvider,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceIdentityProvider> {
    this.activeProviderId = "creating";
    try {
      const provider = await this.rootStore.workspaceSsoService.createProvider(slug, data);
      runInAction(() => {
        this.providers = [...this.providers, provider];
      });
      track("sso_provider_created", {
        provider_id: provider.id,
        provider: data.provider,
        source,
      });
      return provider;
    } finally {
      runInAction(() => {
        this.activeProviderId = null;
      });
    }
  }

  async updateProvider(
    slug: string,
    providerId: string,
    data: Partial<ICreateIdentityProvider>,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceIdentityProvider> {
    this.activeProviderId = providerId;
    // Read before the write: `fields` is what this save changed (NG-2064). Only
    // key names leave the browser, never a secret or a certificate.
    const fields = changedFields(
      data,
      this.providers.find((item) => item.id === providerId),
    );
    try {
      const provider = await this.rootStore.workspaceSsoService.updateProvider(
        slug,
        providerId,
        data,
      );
      runInAction(() => {
        this.providers = this.providers.map((item) => (item.id === provider.id ? provider : item));
      });
      track("sso_provider_updated", {
        provider_id: providerId,
        provider: provider.provider,
        fields,
        source,
      });
      return provider;
    } finally {
      runInAction(() => {
        this.activeProviderId = null;
      });
    }
  }

  async deleteProvider(
    slug: string,
    providerId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeProviderId = providerId;
    const kind = this.providers.find((item) => item.id === providerId)?.provider;
    try {
      await this.rootStore.workspaceSsoService.deleteProvider(slug, providerId);
      runInAction(() => {
        this.providers = this.providers.filter((provider) => provider.id !== providerId);
      });
      track("sso_provider_deleted", { provider_id: providerId, provider: kind, source });
    } finally {
      runInAction(() => {
        this.activeProviderId = null;
      });
    }
  }
}
